import { encodeEntityFolderQuery, encodeAvailableEntityFolderQuery, EntityFolderQuery, ListAvailableEntityFolderQuery } from '@/services/api/resources/entityFolder/query';
import { flow, pipe } from 'fp-ts/function';
import {
  addVersionForUpdate,
  createAuthorizedDeleteRequest,
  createAuthorizedGetRequest,
  createAuthorizedPostRequest, createAuthorizedPutRequest,
  decode, decodeId, decodeVersioned,
  fetchApi, setBody,
  setQuery, Versioned,
} from '@/services/api';
import { chain, map } from 'fp-ts/TaskEither';
import { formCodec, formEditCodec, listCodec, recordCodec } from '@/services/api/resources/entityFolder/codec';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';
import { EntityFolder, EntityFolderEdit } from '@/entities/entityFolder';
import { filterCodec } from '@/services/api/resources/filter';

const baseUrl = '/v2/entity-folders';
const filterUrl = '/v2/filters/entity-folders';
const recordUrl = (id: number): string => `${baseUrl}/${id}`;

export const list = (query: EntityFolderQuery) => pipe(
  `${baseUrl}/list-flat`,
  createAuthorizedGetRequest,
  map(setQuery(encodeEntityFolderQuery(query))),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const listAvailable = (query: ListAvailableEntityFolderQuery) => pipe(
  `${baseUrl}/list-available`,
  createAuthorizedGetRequest,
  map(setQuery(encodeAvailableEntityFolderQuery(query))),
  chain(fetchApi),
  chain(decode(filterCodec)),
);

export const remove = flow(
  recordUrl,
  createAuthorizedDeleteRequest,
  chain(fetchApi),
);

export const create = (entityFolder: EntityFolder) => pipe(
  baseUrl,
  createAuthorizedPostRequest,
  map(setBody(formCodec.encode(entityFolder))),
  chain(fetchApi),
  chain(decodeId),
);

export const update = (id: number, entityFolder: Versioned<EntityFolderEdit>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(entityFolder.version)),
  map(setBody(formEditCodec.encode(entityFolder.data))),
  chain(fetchApi),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(recordCodec)),
);

export const filter = () => pipe(
  filterUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(filterCodec)),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, baseUrl);
